import { toast } from "@devhop/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  CategoryCreateInput,
  CategoryUpdateInput,
} from "@/modules/categories/domain/contracts";
import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import { categoriesApi } from "./client";

export const categoriesKeys = {
  all: ["categories"] as const,
  list: (q: Partial<OffsetPageQueryDTO>) => ["categories", "list", q] as const,
  detail: (id: string) => ["categories", "detail", id] as const,
};

export function useCategoriesQuery(query: Partial<OffsetPageQueryDTO> = {}) {
  const q: OffsetPageQueryDTO = {
    limit: query.limit ?? 20,
    offset: query.offset ?? 0,
    sort: query.sort,
    filters: query.filters,
  };
  return useQuery({
    queryKey: categoriesKeys.list(q),
    queryFn: () => categoriesApi.list(q),
  });
}

export function useCategoryQuery(id: string) {
  return useQuery({
    queryKey: categoriesKeys.detail(id),
    queryFn: () => categoriesApi.get(id),
    enabled: !!id,
  });
}

export function useCreateCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CategoryCreateInput) => categoriesApi.create(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: categoriesKeys.all });
      toast.success("ເພີ່ມຫມວດຫມູ່ສຳເລັດ");
    },
  });
}

export function useUpdateCategory(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CategoryUpdateInput) => categoriesApi.update(id, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: categoriesKeys.detail(id) });
      qc.invalidateQueries({ queryKey: categoriesKeys.all });
      toast.success("ແກ້ໄຂຫມວດຫມູ່ສຳເລັດ");
    },
  });
}

export function useDeleteCategory() {
  const qc = useQueryClient();
  const base = useMutation({
    mutationFn: (id: string) => categoriesApi.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: categoriesKeys.all });
    },
  });

  const run = (id: string) =>
    new Promise<void>((resolve, reject) => {
      base.mutate(id, {
        onSuccess: () => resolve(),
        onError: (e) => reject(e),
      });
    });

  return { ...base, run };
}
